import React from 'react';
import { FormattedMessage } from 'react-intl';
import { hasPermission } from '~/modules/common/permissions';
import getColumnGroups from './resourceAllocationDialogColumns';
import { AssignmentDialogColumns } from './constants';

export const defaultEmptyOption = {
  field: '',
  value: {}
};

export const getDefaultColumns = ({
  me: {
    permissionsMap,
    featureFlags: { isPsaPraaResourceMatchingEnabled }
  },
  isSmartMatchTabSelected
}) => {
  const canViewRequestedCost =
    permissionsMap['urn:replicon:project-action:view-cost-data'] ||
    permissionsMap['urn:replicon:user-action:view-cost-data'];

  const columns = [
    AssignmentDialogColumns.DisplayText,
    AssignmentDialogColumns.Availability,
    canViewRequestedCost
      ? AssignmentDialogColumns.SmartMatchIncludingCost
      : AssignmentDialogColumns.SmartMatchExcludingCost,
    AssignmentDialogColumns.CostRate
  ];

  return columns.filter(col => {
    if (!isPsaPraaResourceMatchingEnabled) return col;

    if (!isSmartMatchTabSelected)
      return (
        col !== AssignmentDialogColumns.SmartMatchIncludingCost &&
        col !== AssignmentDialogColumns.SmartMatchExcludingCost
      );

    return true;
  });
};

export const buildAdditionalDialogColumns = ({ me: { permissionsMap } }) => {
  const isAvailabilityEnabled =
    hasPermission(permissionsMap)({
      actionUri: 'urn:replicon:user-action:view-availability',
      dataAccessLevelUri: 'urn:replicon:user-data-access-level:resource-manager'
    }) ||
    hasPermission(permissionsMap)({
      actionUri: 'urn:replicon:user-action:view-availability',
      dataAccessLevelUri:
        'urn:replicon:user-data-access-level:resource-pool-manager'
    });

  const isViewCostEnabled =
    hasPermission(permissionsMap)({
      actionUri: 'urn:replicon:user-action:view-hourly-cost',
      dataAccessLevelUri: 'urn:replicon:user-data-access-level:resource-manager'
    }) ||
    hasPermission(permissionsMap)({
      actionUri: 'urn:replicon:user-action:view-hourly-cost',
      dataAccessLevelUri:
        'urn:replicon:user-data-access-level:resource-pool-manager'
    });

  return {
    ...(isAvailabilityEnabled
      ? {
          availability: {
            field: AssignmentDialogColumns.Availability,
            value: (
              <FormattedMessage id="resourceAssignmentDialog.availability" />
            ),
            required: true
          }
        }
      : {}),
    ...(isViewCostEnabled
      ? {
          costRate: {
            field: AssignmentDialogColumns.CostRate,
            value: <FormattedMessage id="resourceAssignmentDialog.costRate" />
          }
        }
      : {})
  };
};

export const buildSmartMatchColumns = ({
  me: {
    permissionsMap,
    featureFlags: { isPsaPraaResourceMatchingEnabled }
  },
  isSmartMatchTabSelected
}) => {
  if (isPsaPraaResourceMatchingEnabled) {
    if (!isSmartMatchTabSelected) return {};

    return {
      smartMatchIncludingCost: {
        field: AssignmentDialogColumns.SmartMatchIncludingCost,
        value: (
          <FormattedMessage id="resourceAllocationUserChartDialog.smartMatch" />
        )
      }
    };
  }

  const canViewRequestedCost =
    permissionsMap['urn:replicon:project-action:view-cost-data'] ||
    permissionsMap['urn:replicon:user-action:view-cost-data'];

  return {
    ...(canViewRequestedCost
      ? {
          smartMatchIncludingCost: {
            field: AssignmentDialogColumns.SmartMatchIncludingCost,
            value: (
              <FormattedMessage id="resourceAllocationUserChartDialog.smartMatch" />
            )
          }
        }
      : {}),
    smartMatchExcludingCost: {
      field: AssignmentDialogColumns.SmartMatchExcludingCost,
      value: canViewRequestedCost ? (
        <FormattedMessage id="resourceAllocationUserChartDialog.smartMatchExcludingCost" />
      ) : (
        <FormattedMessage id="resourceAllocationUserChartDialog.smartMatch" />
      )
    }
  };
};

export const buildDefaultDialogColumnConfiguration = ({
  me,
  isSmartMatchTabSelected
}) => {
  const resourceNameColumn = {
    field: 'displayText',
    value: <FormattedMessage id="resourceAssignmentDialog.name" />,
    required: true,
    fixed: true,
    includesNavigationComponent: true
  };

  const permissionBasedColumns = buildAdditionalDialogColumns({ me });
  const smartMatchColumns = buildSmartMatchColumns({
    me,
    isSmartMatchTabSelected
  });

  const allAvailableColumns = {
    displayText: resourceNameColumn,
    ...permissionBasedColumns,
    ...smartMatchColumns
  };

  const defaultColumnOrder = getDefaultColumns({ me, isSmartMatchTabSelected });

  return defaultColumnOrder.map(
    columnKey => allAvailableColumns[columnKey] ?? defaultEmptyOption
  );
};

export const buildTableSettingDialogDefaultColumnConfiguration = ({
  me,
  includeResourceSmartMatchColumn
}) =>
  buildDefaultDialogColumnConfiguration({
    me,
    isSmartMatchTabSelected: includeResourceSmartMatchColumn
  });

export const getAssignmentDialogColumnOptions = ({
  me,
  columnPermissions,
  customFieldDefinitions = [],
  tagFieldData = []
}) => {
  const resourceColumnGroup = getColumnGroups({
    me,
    customFieldDefinitions,
    tagFieldData
  });

  const resourceColumns = resourceColumnGroup || [];

  const dialogColumns = buildAdditionalDialogColumns({
    me
  });

  const dialogColumnArray = Object.values(dialogColumns);

  return [...dialogColumnArray, ...resourceColumns].filter(
    columnOption =>
      columnOption?.field &&
      (columnPermissions[columnOption.field] ||
        (columnOption?.category?.id &&
          columnPermissions[columnOption.category.id]))
  );
};

export const getAvailableSmartMatchColumns = ({
  me,
  isSmartMatchTabSelected
}) => {
  const smartMatchColumns = buildSmartMatchColumns({
    me,
    isSmartMatchTabSelected
  });

  return Object.values(smartMatchColumns);
};

export const getTableSettingSmartMatchDropdownOptions = ({
  me,
  includeResourceSmartMatchColumn
}) =>
  Object.values(
    buildSmartMatchColumns({
      me,
      isSmartMatchTabSelected: includeResourceSmartMatchColumn
    })
  );

export const getAssignmentDialogColumns = ({
  me,
  customFieldDefinitions = [],
  tagFieldData = [],
  isSmartMatchTabSelected
}) => {
  const resourceColumnGroup = getColumnGroups({
    me,
    customFieldDefinitions,
    tagFieldData
  });

  const resourceColumns = resourceColumnGroup || [];

  const defaultColumns = buildAdditionalDialogColumns({
    me
  });

  const defaultColumnArray = Object.values(defaultColumns);
  const smartMatchArray = getAvailableSmartMatchColumns({
    me,
    isSmartMatchTabSelected
  });

  return [
    {
      id: 'assignmentDialogColumns',
      columns: [...smartMatchArray, ...defaultColumnArray, ...resourceColumns]
    }
  ];
};

export const getParsedDialogColumns = ({ columns, allDropdownOptions }) => {
  if (!columns || columns.length === 0) return [];

  return [...columns].map(option => {
    return (
      allDropdownOptions.find(x => x.field === option) || defaultEmptyOption
    );
  });
};

export const filterColumnsByIsSmartMatchTabSelected = ({
  columns,
  isSmartMatchTabSelected,
  isPsaPraaResourceMatchingEnabled
}) =>
  columns.filter(col => {
    if (!isPsaPraaResourceMatchingEnabled) return col;

    return !(
      col === AssignmentDialogColumns.SmartMatchIncludingCost &&
      !isSmartMatchTabSelected
    );
  });
